import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { config } from '../config';
import { getImageUrl } from '../utils/imageUrl';

const emptyEquipment = {
  hero: {
    eyebrow: 'Astro Vino Observatory',
    title: 'Equipment, rigs & scopes',
    intro: '',
    image: ''
  },
  rigs: [],
  gallery: [],
  systems: []
};

const createItem = (type, order = 0) => ({
  id: `${type}-${Date.now()}`,
  title: '',
  tag: type === 'rigs' ? 'Main capture' : '',
  description: '',
  image: type === 'systems' ? undefined : '',
  order
});

function EquipmentAdminPanel({ setSnackbar }) {
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState(emptyEquipment);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingKey, setUploadingKey] = useState('');
  const [error, setError] = useState('');

  const sortedEquipment = useMemo(() => ({
    ...equipment,
    rigs: [...(equipment.rigs || [])].sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
    gallery: [...(equipment.gallery || [])].sort((a, b) => Number(a.order || 0) - Number(b.order || 0)),
    systems: [...(equipment.systems || [])].sort((a, b) => Number(a.order || 0) - Number(b.order || 0))
  }), [equipment]);

  const notify = useCallback((message, severity = 'success') => {
    if (setSnackbar) setSnackbar({ open: true, message, severity });
  }, [setSnackbar]);

  const handleAuthFailure = useCallback((message = 'Your admin session has expired. Please log in again.') => {
    localStorage.removeItem('token');
    localStorage.removeItem('tokenExpiration');
    setError(message);
    notify(message, 'warning');
    navigate('/login');
  }, [navigate, notify]);

  const authHeaders = useCallback((extraHeaders = {}) => {
    const token = localStorage.getItem('token');
    if (!token) {
      handleAuthFailure('Please log in again before updating the Equipment page.');
      return null;
    }
    return {
      ...extraHeaders,
      'Authorization': `Bearer ${token}`
    };
  }, [handleAuthFailure]);

  const throwIfAuthError = useCallback(async (response, fallbackMessage) => {
    if (response.status === 401 || response.status === 403) {
      const data = await response.json().catch(() => ({}));
      const message = data.error?.message || 'Your admin session has expired. Please log in again.';
      handleAuthFailure(message === 'Invalid token' ? 'Your admin session has expired. Please log in again.' : message);
      throw new Error(message);
    }
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.error?.message || fallbackMessage);
    }
  }, [handleAuthFailure]);

  const fetchEquipment = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await fetch(config.endpoints.equipment);
      if (!response.ok) throw new Error('Failed to load equipment data');
      const data = await response.json();
      setEquipment({ ...emptyEquipment, ...data });
    } catch (err) {
      console.error('Equipment fetch error:', err);
      setError(err.message);
      notify(err.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchEquipment();
  }, [fetchEquipment]);

  const saveEquipment = async () => {
    try {
      setSaving(true);
      setError('');
      const headers = authHeaders({ 'Content-Type': 'application/json' });
      if (!headers) return;
      const response = await fetch(config.endpoints.equipment, {
        method: 'PUT',
        headers,
        body: JSON.stringify(sortedEquipment)
      });
      await throwIfAuthError(response, 'Failed to save equipment data');
      const data = await response.json();
      setEquipment({ ...emptyEquipment, ...data });
      notify('Equipment page updated');
    } catch (err) {
      console.error('Equipment save error:', err);
      setError(err.message);
      notify(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateHero = (field, value) => {
    setEquipment(prev => ({ ...prev, hero: { ...prev.hero, [field]: value } }));
  };

  const updateItem = (section, id, field, value) => {
    setEquipment(prev => ({
      ...prev,
      [section]: (prev[section] || []).map(item => item.id === id ? { ...item, [field]: field === 'order' ? Number(value) : value } : item)
    }));
  };

  const addItem = (section) => {
    setEquipment(prev => ({
      ...prev,
      [section]: [...(prev[section] || []), createItem(section, (prev[section] || []).length)]
    }));
  };

  const deleteItem = (section, id) => {
    setEquipment(prev => ({
      ...prev,
      [section]: (prev[section] || []).filter(item => item.id !== id).map((item, index) => ({ ...item, order: index }))
    }));
  };

  const uploadImage = async (file, applyUrl, key) => {
    if (!file) return;
    try {
      setUploadingKey(key);
      const headers = authHeaders();
      if (!headers) return;
      const formData = new FormData();
      formData.append('image', file);
      const response = await fetch(`${config.endpoints.equipment}/upload`, {
        method: 'POST',
        headers,
        body: formData
      });
      await throwIfAuthError(response, 'Failed to upload equipment image');
      const data = await response.json();
      applyUrl(data.url || data.relativeUrl);
      notify('Equipment image uploaded');
    } catch (err) {
      console.error('Equipment image upload error:', err);
      notify(err.message, 'error');
    } finally {
      setUploadingKey('');
    }
  };

  const ImageUploadButton = ({ label, currentImage, onUploaded, uploadKey }) => (
    <Stack spacing={1}>
      {currentImage && (
        <CardMedia
          component="img"
          image={getImageUrl(currentImage)}
          alt={label}
          sx={{ height: 150, objectFit: 'contain', bgcolor: 'rgba(0,0,0,0.35)', borderRadius: 1 }}
        />
      )}
      <Button component="label" variant="outlined" startIcon={uploadingKey === uploadKey ? <CircularProgress size={18} /> : <CloudUploadIcon />} disabled={Boolean(uploadingKey)}>
        {currentImage ? `Replace ${label}` : `Upload ${label}`}
        <input type="file" hidden accept="image/*" onChange={(e) => uploadImage(e.target.files?.[0], onUploaded, uploadKey)} />
      </Button>
      <TextField fullWidth size="small" label="Image URL/path" value={currentImage || ''} onChange={(e) => onUploaded(e.target.value)} />
    </Stack>
  );

  const renderItemEditor = (section, item, index) => (
    <Box key={item.id} sx={{ minWidth: 0 }}>
      <Card sx={{ height: '100%', bgcolor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
        <CardContent>
          <Stack spacing={1.5}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Chip size="small" label={`${section.slice(0, -1)} #${index + 1}`} />
              <IconButton color="error" onClick={() => deleteItem(section, item.id)} aria-label="Delete equipment item">
                <DeleteIcon />
              </IconButton>
            </Stack>
            <TextField fullWidth label="Title" value={item.title || ''} onChange={(e) => updateItem(section, item.id, 'title', e.target.value)} />
            {section === 'rigs' && (
              <TextField fullWidth label="Tag / role" value={item.tag || ''} onChange={(e) => updateItem(section, item.id, 'tag', e.target.value)} />
            )}
            <TextField fullWidth label="Order" type="number" value={item.order ?? index} onChange={(e) => updateItem(section, item.id, 'order', e.target.value)} />
            <TextField fullWidth multiline minRows={3} label="Description" value={item.description || ''} onChange={(e) => updateItem(section, item.id, 'description', e.target.value)} />
            {section !== 'systems' && (
              <ImageUploadButton
                label="image"
                currentImage={item.image || ''}
                uploadKey={`${section}-${item.id}`}
                onUploaded={(url) => updateItem(section, item.id, 'image', url)}
              />
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>Equipment Page Editor</Typography>
          <Typography color="text.secondary">Update the public Equipment page: hero copy, rigs, gallery images, and systems/tooling blocks.</Typography>
        </Box>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={18} /> : <SaveIcon />} onClick={saveEquipment} disabled={saving || Boolean(uploadingKey)}>
          Save equipment page
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.035)' }}>
        <Typography variant="h6" gutterBottom>Top of Equipment page</Typography>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' } }}>
          <Box>
            <Stack spacing={2}>
              <TextField fullWidth label="Eyebrow/chip" value={equipment.hero?.eyebrow || ''} onChange={(e) => updateHero('eyebrow', e.target.value)} />
              <TextField fullWidth label="Page title" value={equipment.hero?.title || ''} onChange={(e) => updateHero('title', e.target.value)} />
              <TextField fullWidth multiline minRows={5} label="Intro text" value={equipment.hero?.intro || ''} onChange={(e) => updateHero('intro', e.target.value)} />
            </Stack>
          </Box>
          <Box>
            <ImageUploadButton label="hero image" currentImage={equipment.hero?.image || ''} uploadKey="hero" onUploaded={(url) => updateHero('image', url)} />
          </Box>
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.035)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Rigs / equipment cards</Typography>
          <Button startIcon={<AddIcon />} onClick={() => addItem('rigs')}>Add rig</Button>
        </Stack>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>{sortedEquipment.rigs.map((item, index) => renderItemEditor('rigs', item, index))}</Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3, bgcolor: 'rgba(255,255,255,0.035)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6">Rig and observatory gallery</Typography>
            <Typography variant="body2" color="text.secondary">These appear as contained/letterboxed equipment reference images on the public page.</Typography>
          </Box>
          <Button startIcon={<AddIcon />} onClick={() => addItem('gallery')}>Add gallery image</Button>
        </Stack>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>{sortedEquipment.gallery.map((item, index) => renderItemEditor('gallery', item, index))}</Box>
      </Paper>

      <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.035)' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography variant="h6">Control, weather and software stack</Typography>
          <Button startIcon={<AddIcon />} onClick={() => addItem('systems')}>Add system</Button>
        </Stack>
        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' } }}>{sortedEquipment.systems.map((item, index) => renderItemEditor('systems', item, index))}</Box>
      </Paper>

      <Divider sx={{ my: 3 }} />
      <Typography variant="caption" color="text.secondary">Remember to click “Save equipment page” after editing text, ordering, or uploaded image assignments.</Typography>
    </Box>
  );
}

export default EquipmentAdminPanel;
